import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of, tap } from 'rxjs';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import { DeleteReferencedDataDialogComponent } from './delete-referenced-data-dialog.component';
import {
	ConfirmDialogModel,
	ConfirmType,
	ConfirmTypes,
	ErrorDialogModel,
	MessageDialogModel,
	MultiButtonDialogComponentDialogModel,
	MultipleUnsavedChangesDialogModel,
	SaveChangesDialogModel,
	SaveChangesResult,
} from './generic-dialogs.model';
import { MessageDialogComponent } from './message-dialog.component';
import { MultiButtonDialogComponent } from './multi-button-dialog.component';
import { MultipleUnsavedChangesDialogComponent } from './multiple-unsaved-changes-dialog.component';
import { SaveChangesDialogComponent } from './save-changes-dialog.component';
import { ServerErrorDialogComponent } from './server-error-dialog.component';

interface ConfirmParams {
	message: string;
	title: string;
	type?: ConfirmType;
	customConfirmCaption?: string;
	maxWidth?: string;
	confirmIsPrimaryColor?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class GenericDialogsService {
	constructor(private dialog: MatDialog) {}

	private saveChangesSingletonObservable: Observable<SaveChangesResult> | null = null;
	private multipleUnsavedChangesSingletonObservable: Observable<SaveChangesResult> | null = null;

	confirm(
		arg1: string | ConfirmParams,
		title?: string,
		type?: ConfirmType,
		customConfirmCaption?: string,
	): Observable<unknown>;
	confirm(opts: {
		message: string;
		title: string;
		type?: ConfirmType;
		customConfirmCaption?: string;
		confirmIsPrimaryColor?: boolean;
		maxWidth?: string;
	}): Observable<unknown>;
	confirm(
		arg1: string | ConfirmParams,
		title?: string,
		type?: ConfirmType,
		customConfirmCaption?: string,
	): Observable<unknown> {
		let params: ConfirmParams;
		if (typeof arg1 === 'string') {
			params = {
				message: arg1,
				title: title ?? 'Confirm',
				type: type || ConfirmTypes.okCancel,
				customConfirmCaption: customConfirmCaption ?? '',
			};
		} else {
			params = arg1;
		}

		const dialogData: ConfirmDialogModel = {
			title: params.title,
			message: params.message,
			type: params.type ?? ConfirmTypes.okCancel,
			customConfirmCaption: params.customConfirmCaption,
			confirmIsPrimaryColor: !!params.confirmIsPrimaryColor,
		};

		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			minWidth: '25%',
			maxWidth: params.maxWidth ?? '60ch',
			data: dialogData,
			autoFocus: true,
			restoreFocus: true,
		});

		return dialogRef.afterClosed();
	}

	message(opts: {
		message: string;
		title: string;
		closeButtonLabel?: string;
		asHtml?: boolean;
		preformatted?: boolean;
		dialogId?: string;
		maxWidth?: string;
	}): Observable<unknown>;
	message(
		message: string,
		title: string,
		options?: {
			closeButtonLabel?: string;
			asHtml?: boolean;
			preformatted?: boolean;
			dialogId?: string;
			maxWidth?: string;
		},
	): Observable<unknown>;
	message(
		arg1:
			| string
			| {
					message: string;
					title: string;
					closeButtonLabel?: string;
					asHtml?: boolean;
					preformatted?: boolean;
					dialogId?: string;
					maxWidth?: string;
			  },
		arg2?: string,
		opts?: {
			closeButtonLabel?: string;
			asHtml?: boolean;
			preformatted?: boolean;
			dialogId?: string;
			maxWidth?: string;
		},
	): Observable<unknown> {
		let message: string;
		let title: string;
		let options: {
			closeButtonLabel?: string;
			asHtml?: boolean;
			preformatted?: boolean;
			dialogId?: string;
			maxWidth?: string;
		};

		if (typeof arg1 === 'string') {
			message = arg1;
			title = arg2 ?? 'Message';
			options = opts || {};
		} else {
			message = arg1.message;
			title = arg1.title;
			options = {
				closeButtonLabel: arg1.closeButtonLabel,
				asHtml: arg1.asHtml,
				preformatted: arg1.preformatted,
				dialogId: arg1.dialogId,
				maxWidth: arg1.maxWidth,
			};
		}

		const dialogData: MessageDialogModel = {
			title,
			message: options?.preformatted ? `<pre>${message}</pre>` : message,
			closeButtonLabel: options.closeButtonLabel || 'Close',
			safeHtml: !!options?.asHtml || !!options?.preformatted,
		};

		// If a dialog ID is provided and that dialog is already open, ignore.
		if (options.dialogId && this.dialog.getDialogById(options.dialogId)) {
			return of(false);
		}

		const dialogRef = this.dialog.open(MessageDialogComponent, {
			id: options.dialogId,
			minWidth: '25%',
			maxWidth: options.maxWidth ?? '60ch',
			data: dialogData,
			autoFocus: true,
			restoreFocus: true,
			//SCS-151
			disableClose: true,
		});

		return dialogRef.afterClosed();
	}

	/**
	 * Displays a message showing that a feature is not yet implemented.
	 * @returns
	 */
	showNotYetImplemented() {
		return this.message('This feature is not yet implemented', 'Not implemented');
	}

	showDeleteReferencedData(message: string, items: string[], title: string) {
		const dialogRef = this.dialog.open(DeleteReferencedDataDialogComponent, {
			minWidth: '25%',
			maxWidth: '60ch',
			data: {
				message,
				items,
				title,
			},
			autoFocus: true,
			restoreFocus: true,
		});

		return dialogRef.afterClosed();
	}

	/**
	 * Displays an internal server error dialog (i.e. 500 status)
	 *
	 * @param {HttpErrorResponse} error
	 * @returns
	 * @memberof GenericDialogsService
	 */
	showServerError(error: HttpErrorResponse) {
		let msg = error.message;
		const devMode = isDevMode();

		msg = 'A data error has occurred. Please contact SMI support for advice and resolution.';

		const dialogData: ErrorDialogModel = {
			title: 'Error',
			message: msg,
			error: error.error,
			devMode,
		};

		const dialogRef = this.dialog.open(ServerErrorDialogComponent, {
			minWidth: '30%',
			maxWidth: '100ch',
			data: dialogData,
			autoFocus: true,
		});

		return dialogRef.afterClosed();
	}

	showMultiButtonDialog(opts: MultiButtonDialogComponentDialogModel): Observable<string> {
		const dialogRef = this.dialog.open(MultiButtonDialogComponent, {
			data: opts,
		});

		return dialogRef.afterClosed();
	}

	/**
	 * Shows a dialog to confirm a delete, returning an Observable.
	 * @param name The name of the object to confirm delete of.
	 */
	showConfirmDeleteDialog(upperName: string, name: string): Observable<boolean> {
		const dialogData: ConfirmDialogModel = {
			title: `Delete ${upperName}`,
			message: `Are you sure you want to delete this ${name}?`,
			type: ConfirmTypes.okCancel,
			customConfirmCaption: 'Delete',
		};

		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			maxWidth: '60ch',
			data: dialogData,
		});

		return dialogRef.afterClosed();
	}

	showSaveChangesDialog(
		typeName: string,
		form?: AbstractControl,
		overrideText?: string,
	): Observable<SaveChangesResult> {
		if (this.saveChangesSingletonObservable) {
			// console.log(
			// 	'%c Returning observable from check-save-changes-dialog',
			// 	'color: red font-size: 0.7rem; font-weight: bold;',
			// );
			return this.saveChangesSingletonObservable;
		}

		// console.log('%c PRESENTING check-save-changes-dialog', 'color: red; font-size: 1rem; font-weight: bold;');

		const dialogData = new SaveChangesDialogModel(typeName, form, overrideText);

		this.saveChangesSingletonObservable = this.dialog
			.open(SaveChangesDialogComponent, {
				minWidth: '25%',
				maxWidth: '60ch',
				data: dialogData,
				autoFocus: true,
				disableClose: true,
			})
			.afterClosed()
			.pipe(
				tap(() => {
					this.saveChangesSingletonObservable = null;
				}),
			);

		return this.saveChangesSingletonObservable;
	}

	showMultipleUnsavedChangesDialog(storeNames: string[]) {
		if (this.multipleUnsavedChangesSingletonObservable) {
			// console.log(
			// 	'%c Returning observable from multiple-unsaved-changes-dialog',
			// 	'color: blue; font-size: 0.7rem; font-weight: bold;',
			// );
			return this.multipleUnsavedChangesSingletonObservable;
		}

		// console.log('%c PRESENTING multiple-unsaved-changes-dialog', 'color: blue; font-size: 1rem; font-weight: bold;');

		const dialogData = new MultipleUnsavedChangesDialogModel(storeNames);

		this.multipleUnsavedChangesSingletonObservable = this.dialog
			.open(MultipleUnsavedChangesDialogComponent, {
				minWidth: '25%',
				maxWidth: '60ch',
				data: dialogData,
				autoFocus: true,
			})
			.afterClosed()
			.pipe(
				tap(() => {
					this.multipleUnsavedChangesSingletonObservable = null;
				}),
			);

		return this.multipleUnsavedChangesSingletonObservable;
	}

	showCantSaveMessage(title = `Can't save`) {
		this.message('Please correct the errors', title);
	}
}
